import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { fetchLiveRatesForMill, withdrawLiveRate } from '../data';

// Shows every currently-live grade offer for this mill — Live or Expired (lifting date
// passed with stock unsold) — directly on the factory card, so an officer editing one
// grade can see at a glance that a sibling grade is stale instead of only discovering it
// later on My Submissions' Expired tab. Withdraw here is the exact same operation as
// My Submissions' Withdraw button (data.js's withdrawLiveRate), just reachable one click
// closer to where the officer is already looking.
const CurrentOffersPanel = ({ millId }) => {
  const [loading, setLoading] = useState(true);
  const [rates, setRates] = useState([]);
  const [error, setError] = useState(null);
  const [withdrawingId, setWithdrawingId] = useState(null);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    if (!millId) return;
    setLoading(true);
    const { rates: r, error: e } = await fetchLiveRatesForMill(millId);
    setRates(r);
    setError(e);
    setLoading(false);
  }, [millId]);

  useEffect(() => { load(); }, [load]);

  const handleWithdraw = async (rate) => {
    const confirmed = window.confirm(`Withdraw the ${rate?.grade} offer? This only removes the offer — the mill itself stays in the system.`);
    if (!confirmed) return;
    setActionError(null);
    setWithdrawingId(rate?.id);
    const { error: e } = await withdrawLiveRate(rate?.id);
    setWithdrawingId(null);
    if (e) {
      setActionError(`Couldn't withdraw ${rate?.grade}: ${e}`);
      return;
    }
    await load();
  };

  if (loading) {
    return (
      <div className="mt-3 rounded-xl border px-4 py-3" style={{ borderColor: '#e8ddd0', background: '#fafaf9' }}>
        <p className="text-xs" style={{ color: '#9ca3af' }}>Loading current offers…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-3 rounded-xl border px-4 py-3" style={{ borderColor: '#fecaca', background: '#fef2f2' }}>
        <p className="text-xs" style={{ color: '#b91c1c' }}>Couldn't load current offers: {error}</p>
      </div>
    );
  }

  if (rates?.length === 0) return null; // nothing live for this mill — no clutter

  const todayStr = new Date()?.toISOString()?.slice(0, 10);

  return (
    <div className="mt-3 rounded-xl border overflow-hidden" style={{ borderColor: '#e8ddd0' }}>
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: '#fafaf9', borderBottom: '1px solid #e8ddd0' }}>
        <Icon name="Tag" size={14} color="#f59e0b" />
        <span className="text-sm font-bold" style={{ color: '#1f2937' }}>Current Offers ({rates?.length})</span>
      </div>

      <div className="divide-y" style={{ borderColor: '#f3e8d0' }}>
        {rates?.map((r) => {
          const sold = Number(r?.available_qty) <= 0;
          const expired = !sold && r?.lifting_date && r?.lifting_date < todayStr;
          return (
            <div key={r?.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold" style={{ color: '#1f2937' }}>{r?.grade}</span>
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                    style={
                      expired
                        ? { background: '#ffedd5', color: '#9a3412' }
                        : { background: '#dcfce7', color: '#166534' }
                    }
                  >
                    {expired ? 'Expired' : 'Live'}
                  </span>
                </div>
                <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>
                  ₹{Number(r?.rate_excl_gst)?.toLocaleString('en-IN')}/MT excl. GST · {r?.available_qty} MT · Lifting {r?.lifting_date ? new Date(r?.lifting_date)?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                </p>
              </div>
              {expired && (
                <button
                  onClick={() => handleWithdraw(r)}
                  disabled={withdrawingId === r?.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors duration-150 disabled:opacity-60"
                  style={{ color: '#9a3412', borderColor: '#fed7aa', background: '#fff' }}
                >
                  <Icon name="Trash2" size={12} color="#9a3412" />
                  {withdrawingId === r?.id ? 'Withdrawing…' : 'Withdraw'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {actionError && (
        <div className="px-4 py-2.5 text-xs" style={{ background: '#fef2f2', color: '#b91c1c', borderTop: '1px solid #fecaca' }}>
          {actionError}
        </div>
      )}
    </div>
  );
};

export default CurrentOffersPanel;
